import { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/apiClient';
import { analyticsApiClient } from '../api/analyticsApi';
import type { ColumnMeta } from '../types/reportBuilder';

interface UseReportBuilderWidgetDataProps {
  reportBuilderId?: string;
  scopingType?: string;
  enabled?: boolean;
}

interface UseReportBuilderWidgetDataResult {
  data: any;
  columns: ColumnMeta[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch data for report builder widgets.
 *
 * Step 1: Fetches the saved report config from the core backend.
 * Step 2: Executes the query via the analytics API using the report's query spec.
 */
export const useReportBuilderWidgetData = ({
  reportBuilderId,
  scopingType,
  enabled = true,
}: UseReportBuilderWidgetDataProps): UseReportBuilderWidgetDataResult => {
  const [data, setData] = useState<any>(null);
  const [columns, setColumns] = useState<ColumnMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !reportBuilderId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Step 1: Fetch the saved report config
        const reportResponse = await apiClient.get(`/report-builder/${reportBuilderId}`);
        const report = reportResponse.data?.data || reportResponse.data;
        const querySpec = report?.query_config || report?.query_spec;

        if (!querySpec) {
          setError('Report has no query configuration');
          setLoading(false);
          return;
        }

        // Step 2: Execute the query via the analytics API.
        // If a scopingType is explicitly provided by the widget, use it.
        // Otherwise, strip the saved scoping_type from the query spec so the
        // backend falls back to "self" scope (every user is authorised for self).
        // This prevents 403 errors when the viewer doesn't have the same
        // scope permissions as the report creator.
        const { scoping_type: _savedScope, ...specWithoutScope } = querySpec;
        const queryPayload = {
          ...specWithoutScope,
          ...(scopingType ? { scoping_type: scopingType } : {}),
        };

        const queryResponse = await analyticsApiClient.post('/report-builder/query', queryPayload);
        const result = queryResponse.data;

        setData(result.data || result.rows || result);
        setColumns(result.columns ?? result.schema?.columns ?? []);
      } catch (err: any) {
        console.error('Failed to fetch report builder widget data:', err);
        const errorMsg = err?.response?.data?.details || err?.response?.data?.error || err.message || 'Failed to load report data';
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [enabled, reportBuilderId, scopingType, fetchCount]);

  return { data, columns, loading, error, refetch };
};
